/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import * as storageUtils from '@/utils/storageUtils';
import { browserLanguage } from '@/commons';
import { UserDto } from '@/commons/dtos';
import { RootState } from '@/store/reducer';
import { ControlTypeContent, IStep, PhaseState, TimeFormat, UserSettings } from '@/types';
import { checkEmptyValueInObject, isEmpty, JSONObjectDeepEqual } from '@/utils/utils';
import axiosInstance from '@/utils/axiosInstance';

interface ProductState {
    productName: string;
}

interface SKUState {
    SKUId: number | null;
}

interface PhaseContent {
    name: string;
    id: string;
    displayOrder: number;
    translationNameKey: string;
}

interface StepContent {
    stepName: string;
    id: string;
    translationNameKey?: string;
    translationTitleKey?: string;
    learnMoreContent?: string;
    phaseId?: string;
    answered: boolean;
    description: string;
    displayOrder: number;
    name: string;
}

interface AdditionalUser {
    users: UserDto[];
    error: any;
    openErrorDialog: boolean;
}

type InitialState = {
    currentProduct: ProductState;
    currentSKU: SKUState;
    aPhases: PhaseState[];
    aSubStepsForCurrentPhase: IStep[];
    currentProductConfigurationValue: any;
    currentRunId: string;
    currentSubStep: string;
    readOnlyMode: boolean;
    currentProductConfigKeys: any;
    currentFlowId: string;
    currentPhase: PhaseContent;
    currentStep: StepContent;
    currentControlType: ControlTypeContent;
    commodityCode: any;
    csvUploaded: boolean;
    additionalUsers: AdditionalUser;
    unansweredQuestions: boolean;
    showWelcomeVideo: boolean;
    showHelpVideo: boolean;
    isLaunchTriggeredFromUI: boolean;
    welcomeVideoLink: string;
    isFinalPage: boolean;
    userSettings: UserSettings;
    currentRealm: string | undefined | null; // Only personalization has realm, integration does not have realm
    isJouleEnabled: boolean;
    isCleanUpButtonEnabled?: boolean;
};

interface NavigationData {
    flowId: string;
}

interface SubNavigationData {
    phaseId: string;
}

interface AdditionalUserData {
    runId: string;
}

export const fetchNavigationData = createAsyncThunk('fetchNavigationData', async ({ flowId }: NavigationData) => {
    try {
        const response = await axiosInstance.get(`/phases/flow/${flowId}`);
        const allPhases: any = response.data;
        return allPhases
            .filter((phase: any) => phase.flowId === flowId)
            .sort((phase1: any, phase2: any) => phase1.displayOrder - phase2.displayOrder);
    } catch (error) {
        console.error('Error loading JSON data:', error);
    }
});

export const fetchSubNavigationData = createAsyncThunk('fetchSubNavigationData', async ({ phaseId }: SubNavigationData) => {
    try {
        const response = await axiosInstance.get(`/steps/phase/${phaseId}`);
        const data: any = response.data;
        return data.filter((elm: any) => elm.phaseId === phaseId).sort((step1: any, step2: any) => step1.displayOrder - step2.displayOrder);
    } catch (error) {
        console.error('Error loading JSON data:', error);
    }
});

export const fetchStepsByPhaseIdAndRunId = createAsyncThunk('fetchStepsByPhaseId', async ({ phaseId, runId }: { phaseId: string; runId: string }) => {
    try {
        const response = await axiosInstance.get(`/steps/runId/${runId}/phase/${phaseId}`);
        const data: any = response.data;
        return data.sort((step1: any, step2: any) => step1.displayOrder - step2.displayOrder);
    } catch (error) {
        console.error('Error loading JSON data:', error);
    }
});

export const fetchFlowByProductId = createAsyncThunk('fetchFlowByProductId', async ({ productId }: any) => {
    try {
        const response = await axiosInstance.get(`/flows/byProduct/${productId}`);
        return response.data;
    } catch (error) {
        console.error(`Error loading Flow by product Id: ${productId}`, error);
    }
});

export const fetchRunData = createAsyncThunk('fetchRunData', async ({ flow }: any, { rejectWithValue }) => {
    try {
        const user = storageUtils.getItem('user');
        const response = await axiosInstance.get(`/product-runs/user/${user.user_uuid}?flowId=${flow.id}`);
        const data: any = response.data;
        return data.id;
    } catch (error) {
        console.error('Product run not found for the user.');
        return rejectWithValue('Product run not found for the user.');
    }
});

export const fetchTasks = createAsyncThunk('fetchTasks', async (params: string, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/tasks/?${params}`);

        return response.data;
    } catch (error) {
        console.error('Error loading tasks:', error);
    }
});

export const createProductRun = createAsyncThunk('createProductRun', async ({ flow, sku }: any, { rejectWithValue }) => {
    try {
        const userId = storageUtils.getItem('user').user_uuid;

        const runIdJsonBody = {
            flowId: flow.id,
            userId: userId,
            orgId: '',
            realm: '',
            sku: sku,
            customerId: userId,
            createdBy: userId,
            updatedBy: userId,
        };
        const productRun: any = await axiosInstance.post(`product-runs/`, runIdJsonBody);
        return productRun.data?.id;
    } catch (error) {
        console.error('Failed to create product Run.');
        return rejectWithValue('Failed to create product Run.');
    }
});

/**
 * @deprecated - Will be removed soon after solve ViewReportPage
 */
export const fetchAdditionalUsers = createAsyncThunk('fetchAdditionalUsers', async ({ runId }: AdditionalUserData) => {
    try {
        const queryParams = {
            runId: runId,
        };
        const queryString = new URLSearchParams(queryParams).toString();

        const response = await axiosInstance.get(`/user/?${queryString}`);
        if (response.status >= 200 && response.status < 300) {
            const data: any = response.data;
            if (data) {
                return data;
            } else {
                throw new Error('Error loading additional user data');
            }
        } else {
            throw new Error('Network response was not ok');
        }
    } catch (error) {
        console.error('Error loading additional user data:', error);
    }
});

export const updateConfigValue = createAsyncThunk('updateConfigValue', async (force: boolean, { getState, dispatch }) => {
    const state = getState() as RootState;
    const currentProductConfigurationValue = state.productList.currentProductConfigurationValue;
    const configurationValue = currentProductConfigurationValue.configurationValue;

    let isObject = false;
    try {
        const parsedValue = JSON.parse(configurationValue);
        isObject = typeof parsedValue === 'object' && parsedValue !== null;
    } catch {
        isObject = false;
    }

    if (configurationValue != '' && isEmpty(configurationValue)) {
        return null;
    }
    if (!force && configurationValue === currentProductConfigurationValue?.personalizationConfiguration?.configurationValue) {
        return null;
    }
    try {
        const response = await axiosInstance.post(`/product-configurations/`, {
            ...currentProductConfigurationValue,
        });

        const isAnsweredEmpty = isObject ? checkEmptyValueInObject(configurationValue) : isEmpty(configurationValue);
        if (isAnsweredEmpty) {
            dispatch(actions.setSubStepAnswered(false));
        } else {
            dispatch(actions.setSubStepAnswered(true));
        }
        return response.data;
    } catch (error) {
        console.error('Error updating configuration value:', error);
    }
});

export const purgeExistingData = createAsyncThunk('purgeExistingData', async (_, { getState }) => {
    try {
        const state = getState() as RootState;
        const productList = state.productList;
        const response = await axiosInstance.delete(`/commodity-codes-run/purge/${productList.currentRunId}`);
        return response.data;
    } catch (error) {
        console.error('Error purging existing data:', error);
    }
});

export const selectIsProductConfigurationChanged = createSelector(
    (state: RootState) => state.productList.currentProductConfigurationValue,
    currentProductConfigurationValue => {
        const configurationValue = currentProductConfigurationValue?.configurationValue;
        const personalizationValue = currentProductConfigurationValue?.personalizationConfiguration?.configurationValue;
        let isObject = false;
        let parsedConfigurationValue = null;
        let parsedPersonalizationValue = null;
        try {
            parsedConfigurationValue = JSON.parse(configurationValue);
            isObject = typeof parsedConfigurationValue === 'object' && parsedConfigurationValue !== null && parsedConfigurationValue !== undefined;
            parsedPersonalizationValue = JSON.parse(personalizationValue);
        } catch {
            isObject = false;
        }

        if (!configurationValue && !personalizationValue) {
            return false;
        }
        if (isObject === true) {
            return !JSONObjectDeepEqual(parsedConfigurationValue, parsedPersonalizationValue);
        } else {
            return configurationValue !== personalizationValue;
        }
    }
);

export const userSettingsInitialState: UserSettings = {
    locale: storageUtils.getItem('user')?.locale || browserLanguage,
    selectedLocal: storageUtils.getItem('user')?.locale || browserLanguage,
    timeFormat: storageUtils.getItem('user')?.time_format || TimeFormat.TWELVE_HOUR,
    selectedTimeFormat: storageUtils.getItem('user')?.time_format || TimeFormat.TWELVE_HOUR,
    isValid: true,
};

const initialState: InitialState = {
    currentProduct: {
        productName: '',
    },
    currentSKU: {
        SKUId: null,
    },
    aPhases: [],
    aSubStepsForCurrentPhase: [],
    currentProductConfigurationValue: [],
    currentRunId: '',
    currentSubStep: '',
    readOnlyMode: false,
    currentProductConfigKeys: [],
    currentFlowId: '',
    currentPhase: {
        id: '',
        name: '',
        displayOrder: 0,
        translationNameKey: '',
    },
    currentStep: {
        stepName: '',
        id: '',
        translationNameKey: '',
        translationTitleKey: '',
        learnMoreContent: '',
        phaseId: '',
        answered: false,
        description: '',
        displayOrder: 0,
        name: '',
    },
    currentControlType: {
        id: '',
        name: '',
        orientation: '',
        controlTypeOptions: [],
    },
    commodityCode: {
        commodityCodeType: '',
        codes: [],
    },
    csvUploaded: false,
    additionalUsers: {
        users: [],
        error: '',
        openErrorDialog: false,
    },
    unansweredQuestions: false,
    showWelcomeVideo: false,
    showHelpVideo: false,
    isLaunchTriggeredFromUI: false,
    welcomeVideoLink: '',
    isFinalPage: false,
    userSettings: userSettingsInitialState,
    currentRealm: '',
    isJouleEnabled: false,
    isCleanUpButtonEnabled: false,
};

const productSlice = createSlice({
    name: 'products',
    initialState,
    reducers: {
        resetState: state => {
            // Reset all fields to initial state
            Object.assign(state, initialState);
        },
        setCurrentProduct: function (state, action: PayloadAction<ProductState>) {
            state.currentProduct.productName = action.payload.productName;
        },
        setCurrentSKU: function (state, action: PayloadAction<SKUState>) {
            state.currentSKU.SKUId = action.payload.SKUId;
        },
        setCurrentRealm: function (state, action: PayloadAction<string | undefined | null>) {
            state.currentRealm = action.payload;
        },
        setCurrentProductConfigurationValue: function (state, action: PayloadAction<any>) {
            state.currentProductConfigurationValue = action.payload;
        },
        setCurrentSubStepID: function (state, action: PayloadAction<string>) {
            state.currentSubStep = action.payload;
        },
        setReadOnlyMode: function (state, action: PayloadAction<boolean>) {
            state.readOnlyMode = action.payload;
        },
        setCurrentProductConfigKeys: function (state, action: PayloadAction<any>) {
            const index = state.currentProductConfigKeys.findIndex((elm: any) => elm.key === action.payload.key);
            if (index === -1) {
                state.currentProductConfigKeys.push(action.payload);
            } else
                state.currentProductConfigKeys = [
                    ...state.currentProductConfigKeys.slice(0, index),
                    { ...state.currentProductConfigKeys[index], value: action.payload.value },
                    ...state.currentProductConfigKeys.slice(index + 1),
                ];
        },
        setCurrentFlowId: function (state, action: PayloadAction<string>) {
            state.currentFlowId = action.payload;
        },
        setCurrentPhase: function (state, action: PayloadAction<PhaseContent>) {
            state.currentPhase = action.payload;
        },
        setNextStep: function (state) {
            const currentStepIndex = state.aSubStepsForCurrentPhase.findIndex((step: IStep) => step.id === state.currentStep.id);

            if (currentStepIndex < state.aSubStepsForCurrentPhase.length - 1) {
                const nextStep = state.aSubStepsForCurrentPhase[currentStepIndex + 1];
                state.currentStep = nextStep;
            } else {
                const currentPhaseIndex = state.aPhases.findIndex((phase: PhaseState) => phase.id === state.currentPhase.id);
                if (currentPhaseIndex < state.aPhases.length - 1) {
                    const nextPhase = state.aPhases[currentPhaseIndex + 1];
                    state.currentPhase = nextPhase;
                }
            }
        },
        setCurrentControlType: function (state, action: PayloadAction<ControlTypeContent>) {
            state.currentControlType = {
                id: action.payload?.id,
                name: action.payload?.name,
                orientation: action.payload?.orientation,
                controlTypeOptions: action.payload?.controlTypeOptions,
            };
        },
        setPreviousStep: function (state) {
            const currentStepIndex = state.aSubStepsForCurrentPhase.findIndex((step: IStep) => step.id === state.currentStep.id);
            if (currentStepIndex > 0) {
                const previousStep = state.aSubStepsForCurrentPhase[currentStepIndex - 1];
                state.currentStep = previousStep;
            } else {
                const currentPhaseIndex = state.aPhases.findIndex((phase: PhaseState) => phase.id === state.currentPhase.id);
                if (currentPhaseIndex > 0) {
                    const previousPhase = state.aPhases[currentPhaseIndex - 1];
                    state.currentPhase = previousPhase;
                }
            }
        },
        setCurrentPhaseById: function (state, action: PayloadAction<{ id: string }>) {
            const phase = state.aPhases.find((phase: PhaseState) => phase.id === action.payload.id);
            if (phase) {
                state.currentPhase = phase;
                state.isLaunchTriggeredFromUI = false;
                state.isFinalPage = false;
            }
        },
        setCurrentStep: function (state, action: PayloadAction<string>) {
            const step = state.aSubStepsForCurrentPhase.find((step: IStep) => step.id === action.payload);
            if (step) {
                state.currentStep = step;
            }
        },
        setCsvUploaded: function (state, action: PayloadAction<boolean>) {
            state.csvUploaded = action.payload;
        },
        setCommodityCode: function (state, action: PayloadAction<any>) {
            state.commodityCode.commodityCodeType = action.payload.commodityCodeType;
            state.commodityCode.codes = action.payload.codes;
        },
        setUsers: function (state, action: PayloadAction<any>) {
            state.additionalUsers.users = [...action.payload];
        },
        setUsersError: function (state, action: PayloadAction<any>) {
            state.additionalUsers.error = action.payload;
        },
        setUsersDialogState: function (state, action: PayloadAction<any>) {
            state.additionalUsers.openErrorDialog = action.payload;
        },
        setCurrentRunId: function (state, action: PayloadAction<any>) {
            state.currentRunId = action.payload;
        },
        setShowHelpVideo: function (state, action: PayloadAction<boolean>) {
            state.showHelpVideo = action.payload;
        },
        setUnansweredQuestions: function (state, action: PayloadAction<boolean>) {
            state.unansweredQuestions = action.payload;
        },
        setShowWelcomeVideo: function (state, action: PayloadAction<boolean>) {
            state.showWelcomeVideo = action.payload;
        },
        setLaunchTriggeredFromUI: function (state, action: PayloadAction<boolean>) {
            state.isFinalPage = true;
            state.isLaunchTriggeredFromUI = action.payload;
        },
        setIsFinalPage: function (state, action: PayloadAction<boolean>) {
            state.isFinalPage = action.payload;
        },

        setUserSettings: function (state, action: PayloadAction<UserSettings>) {
            state.userSettings = action.payload;
        },
        setSubStepAnswered: function (state, action: PayloadAction<boolean>) {
            const currentStepIndex = state.aSubStepsForCurrentPhase.findIndex((step: IStep) => step.id === state.currentStep.id);
            state.aSubStepsForCurrentPhase[currentStepIndex].answered = action.payload;
        },
        setIsJouleEnabled: function (state, action: PayloadAction<boolean>) {
            state.isJouleEnabled = action.payload;
        },
        setIsCleanUpButtonEnabled: function (state, action: PayloadAction<boolean>) {
            state.isCleanUpButtonEnabled = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchNavigationData.fulfilled, (state, action) => {
            state.aPhases = action.payload;
        });
        builder.addCase(fetchRunData.fulfilled, (state, action) => {
            state.currentRunId = action.payload;
        });
        builder.addCase(createProductRun.fulfilled, (state, action) => {
            state.currentRunId = action.payload;
        });
        builder.addCase(fetchFlowByProductId.fulfilled, (state, action) => {
            state.currentFlowId = action.payload.id;
        });

        builder.addCase(fetchSubNavigationData.fulfilled, (state, action) => {
            state.aSubStepsForCurrentPhase = action.payload;
        });
        builder.addCase(fetchStepsByPhaseIdAndRunId.fulfilled, (state, action) => {
            state.aSubStepsForCurrentPhase = action.payload;
            const isSamePhase = state.currentStep.phaseId === state.currentPhase.id;
            if (!isSamePhase) {
                const currentPhaseIndex = state.aPhases.findIndex((phase: PhaseState) => phase.id === state.currentStep.phaseId);
                const nextPhaseIndex = state.aPhases.findIndex((phase: PhaseState) => phase.id === action.meta.arg.phaseId);
                const isPreviousDirection = nextPhaseIndex < currentPhaseIndex;
                state.currentStep = isPreviousDirection ? action.payload[action.payload.length - 1] : action.payload[0];
            }
        });
        builder.addCase(fetchAdditionalUsers.fulfilled, (state, action) => {
            state.additionalUsers.users = action.payload;
        });
    },
});

export const actions = productSlice.actions;
export { productSlice };
export default productSlice.reducer;
